import { useState, useContext } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Switch,
  Input,
  Select,
  Button,
  Badge,
  Loader,
} from '@livechat/design-system'
import ordinal from 'ordinal'
import validator from 'validator'

import api from '../api'
import { Context } from '../context'
import closeIcon from '../assets/icons/delete.svg'

const ChatAttributes = ({ survey, onChange, status, disabled }) => {
  const { surveyToken, addAttributeName, deleteAttributeName } = useContext(
    Context
  )

  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [attrName, setAttrName] = useState('')
  const [errors, setErrors] = useState({ question: null, name: null })
  const [loading, setLoading] = useState()

  const savedAttributes = survey?.attributes
    ? Object.keys(survey?.attributes).map((el) => Number(el)) || []
    : []

  const items = survey.structure
    .map((field, index) => ({
      key: index,
      props: { name: `${index + 1}. ${field.question}`, value: index },
    }))
    .filter((el) => {
      return !savedAttributes.includes(el.props.value)
    })

  const getItemBody = (props) => {
    if (!props) {
      return null
    }
    return <div id={props.value}>{props.name}</div>
  }

  const getSelectedItemBody = (props) => {
    return <div id={props.value}>{props.name}</div>
  }

  const onSubmit = async () => {
    const newErrors = {
      name:
        !attrName || validator.isEmpty(attrName, { ignore_whitespace: true }),
      question: !Number.isInteger(selectedQuestion),
    }

    setErrors(newErrors)

    if (Object.values(newErrors).every((err) => !err)) {
      setLoading('add')
      const resp = await api.createAttributeName(
        surveyToken,
        survey.id,
        selectedQuestion,
        attrName
      )

      if (resp === 'OK') {
        addAttributeName(survey.id, selectedQuestion, attrName)
      }
      setLoading()
      setSelectedQuestion(null)
      setAttrName('')
    }
  }

  const onDelete = async (index) => {
    setLoading(`delete-${index}`)
    const resp = await api.deleteAttributeName(surveyToken, survey.id, index)

    if (resp === 'OK') {
      deleteAttributeName(survey.id, index)
    }
    setLoading()
  }

  return (
    <div>
      <div
        css={css`
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        `}
      >
        <Switch
          size="compact"
          onChange={onChange}
          on={status || false}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
        />
        <span
          css={css`
            font-size: 16px;
            display: inline-block;
            margin-left: 5px;
            font-weight: 300;
          `}
        >
          Saving attributes is {status ? 'enabled' : 'disabled'}.{' '}
        </span>
      </div>
      {status && (
        <div
          css={css`
            ${savedAttributes.length > 0 && `margin-top: 20px;`}
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              font-weight: 300;
              margin-bottom: 10px;
            `}
          >
            {survey?.attributes &&
              Object.keys(survey.attributes).map((el, index) => {
                return (
                  <div
                    key={`attr-${index}`}
                    css={css`
                      margin-bottom: 10px;
                      display: flex;
                    `}
                  >
                    <Badge>{index + 1}</Badge>{' '}
                    <span>
                      Answer for{' '}
                      <span
                        css={css`
                          font-weight: 500;
                        `}
                      >
                        {ordinal(Number(el) + 1)}
                      </span>{' '}
                      question as{' '}
                      <span
                        css={css`
                          font-weight: 500;
                        `}
                      >
                        {survey?.attributes[el]}
                      </span>
                      .
                    </span>
                    {loading === `delete-${Number(el)}` ? (
                      <Loader
                        size="small"
                        css={css`
                          margin-left: 3px;
                        `}
                      />
                    ) : (
                      <img
                        src={closeIcon}
                        onClick={() => onDelete(Number(el))}
                        alt="close"
                        draggable="false"
                        css={css`
                          width: 20px;
                          height: 20px;
                          margin-top: -1px;
                          margin-left: 2px;
                          cursor: pointer;
                        `}
                      />
                    )}
                  </div>
                )
              })}
          </div>
          {items.length > 0 && (
            <div
              css={css`
                margin-top: 5px;
                display: flex;
                align-items: center;
                font-weight: 300;
                margin-bottom: 10px;

                .lc-select-head {
                  height: 19px;
                }

                .lc-input {
                  font-weight: 300;
                }
              `}
            >
              <div
                css={css`
                  margin-right: 5px;
                `}
              >
                Save answer for
              </div>
              <Select
                css={css`
                  width: 35%;
                `}
                id="select-example"
                items={items}
                searchProperty="name"
                onItemSelect={setSelectedQuestion}
                getItemBody={getItemBody}
                search
                placeholder="Select question"
                getSelectedItemBody={getSelectedItemBody}
                selected={selectedQuestion}
                searchPlaceholder="Search..."
                error={errors.question}
              />
              <div
                css={css`
                  margin: 0 5px;
                `}
              >
                as
              </div>
              <Input
                value={attrName}
                onChange={(e) => setAttrName(e.target.value)}
                placeholder="Type name"
                error={errors.name ? 'error' : undefined}
              />
              <Button
                onClick={onSubmit}
                css={css`
                  margin-left: 10px;
                `}
                loading={loading === 'add'}
              >
                Add
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ChatAttributes
